import { useEffect, useRef, useState } from 'react';
import { RotateCcw, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { streamJobEvents, type SseEvent } from '../api/sse';
import { applyDeployEvent, INITIAL_STATE, type DeployProgressState } from './deployProgressState';

const STAGE_LABELS: Record<string, string> = {
  pending: '准备中',
  adapting: '适配中',
  deploying: '部署中',
  completed: '已完成',
  failed: '失败',
};

const STAGES = ['pending', 'adapting', 'deploying', 'completed'];

interface DeployProgressProps {
  /** 部署任务 ID（POST /deployments 返回的 job_id）；组件据此先拉历史事件重建进度，再接 SSE 实时流 */
  jobId: string;
  onRetry?: () => void;
  /** 任务到达终态时回调（成功/失败 + 错误信息） */
  onSettled?: (status: 'success' | 'failed', error?: string | null) => void;
}

export default function DeployProgress({ jobId, onRetry, onSettled }: DeployProgressProps) {
  const [progress, setProgress] = useState<DeployProgressState>(INITIAL_STATE);
  const [streamError, setStreamError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const settledRef = useRef(false);
  const onSettledRef = useRef(onSettled);

  // P11 事件驱动：先拉历史事件重建进度（页面刷新不丢进度），再接 SSE 实时流
  useEffect(() => {
    // 同步 ref 与最新 props（不能在 render 阶段写 ref，见 react-hooks/refs）
    onSettledRef.current = onSettled;
    // 通过本地包装触发重置，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
    const tick = () => {
      setProgress(INITIAL_STATE);
      setStreamError('');
    };
    tick();
    settledRef.current = false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const handleEvent = (event: SseEvent) => {
      setProgress((prev) => {
        const next = applyDeployEvent(prev, event);
        if (event.type === 'done' && !settledRef.current) {
          settledRef.current = true;
          onSettledRef.current?.(next.status === 'success' ? 'success' : 'failed', next.error);
        }
        return next;
      });
    };

    streamJobEvents(jobId, handleEvent, controller.signal).catch((e) => {
      if (e?.name === 'AbortError') return;
      // 进度流中断不等于部署失败：诚实提示，进度仍停留在最后已知事件
      setStreamError(e?.message || '订阅部署进度失败');
    });

    return () => controller.abort();
    // onSettled 通过 onSettledRef 读取最新值，订阅本身只依赖 jobId（重启订阅会丢失进度流）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const { stage, status, error, verification, verificationSummary } = progress;
  const currentIndex = stage ? STAGES.indexOf(stage) : 0;
  const isFailed = status === 'failed';

  return (
    <div className="card" style={{ background: 'var(--bg-primary)', padding: 20 }}>
      <h4 style={{ margin: '0 0 16px', fontSize: 14 }}>部署进度</h4>

      <div className="flex items-center" style={{ gap: 0 }}>
        {STAGES.map((s, i) => {
          const isCompleted = i < currentIndex;
          const isActive = i === currentIndex && !isFailed;
          const label = STAGE_LABELS[s] || s;

          return (
            <div key={s} className="flex items-center" style={{ flex: 1 }}>
              <div className="flex flex-col items-center" style={{ flex: 1 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isCompleted
                      ? 'var(--color-success)'
                      : isActive
                      ? 'var(--color-primary)'
                      : 'var(--bg-tertiary)',
                    color: isCompleted || isActive ? 'white' : 'var(--text-muted)',
                    fontSize: 12,
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isCompleted ? (
                    <CheckCircle2 size={16} />
                  ) : isActive ? (
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    i + 1
                  )}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 6,
                    color: isCompleted
                      ? 'var(--color-success)'
                      : isActive
                      ? 'var(--color-primary)'
                      : 'var(--text-muted)',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {label}
                </div>
              </div>
              {i < STAGES.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    background:
                      i < currentIndex ? 'var(--color-success)' : 'var(--border-default)',
                    margin: '0 8px',
                    marginBottom: 16,
                    transition: 'background 0.3s ease',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {isFailed && (
        <div
          className="flex flex-col gap-3 mt-4"
          style={{
            padding: 12,
            background: 'rgba(248,81,73,0.1)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-error)',
          }}
        >
          <div className="flex items-center gap-2">
            <AlertCircle size={16} style={{ color: 'var(--color-error)' }} />
            <span style={{ color: 'var(--color-error)', fontSize: 13, fontWeight: 500 }}>
              部署失败
            </span>
          </div>
          {error && (
            <p style={{ color: 'var(--color-error)', fontSize: 12, margin: 0 }}>
              {error}
            </p>
          )}
          {onRetry && (
            <button className="secondary" onClick={onRetry} style={{ alignSelf: 'flex-start' }}>
              <RotateCcw size={14} /> 重试
            </button>
          )}
        </div>
      )}

      {status === 'success' && (
        <div
          className="flex items-center gap-2 mt-4"
          style={{
            padding: 12,
            background: 'rgba(126,231,135,0.1)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-success)',
          }}
        >
          <CheckCircle2 size={16} style={{ color: 'var(--color-success)' }} />
          <span style={{ color: 'var(--color-success)', fontSize: 13, fontWeight: 500 }}>
            部署成功
          </span>
        </div>
      )}

      {/* B13 部署后确定性验证结论行（verifying / completed stage 事件携带） */}
      {verification && (
        <div
          className="flex flex-col gap-1 mt-3"
          style={{
            padding: 12,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            background: 'var(--bg-secondary)',
          }}
        >
          <div className="flex items-center gap-2">
            {verification.healthy ? (
              <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} />
            ) : (
              <AlertCircle size={15} style={{ color: 'var(--color-warning)' }} />
            )}
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              验证结论：{verification.healthy ? '健康检查通过' : '健康检查未通过'}
            </span>
            {typeof verification.status_code === 'number' && (
              <span className="text-muted" style={{ fontSize: 12 }}>
                HTTP {verification.status_code}
              </span>
            )}
          </div>
          {verificationSummary && (
            <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
              {verificationSummary}
            </p>
          )}
          {verification.error && !verification.healthy && (
            <p style={{ fontSize: 12, margin: 0, color: 'var(--color-warning)' }}>{verification.error}</p>
          )}
          {verification.suggestion && !verification.healthy && (
            <p style={{ fontSize: 12, margin: 0 }}>修复建议：{verification.suggestion}</p>
          )}
        </div>
      )}

      {streamError && (
        <div className="flex items-center gap-2 mt-4" style={{ fontSize: 12, color: 'var(--color-warning)' }}>
          <AlertCircle size={14} />
          <span>{streamError}</span>
        </div>
      )}
    </div>
  );
}
